import React, {
  createContext, useState, useContext, useEffect, useMemo,
} from 'react';

// Create a context for managing privilege-related data
const PrivilegeContext = createContext();

// Function used to get the first available module for the user
export function getFirstEnabledModule(modules = {}) {
  if (typeof modules !== 'object' || modules === null) {
    return null; // Return null for invalid input instead of throwing an error
  }

  const moduleEntries = Object.entries(modules);
  const foundModule = moduleEntries
    .map(([, moduleValues]) => {
      if (moduleValues && typeof moduleValues === 'object') {
        const entries = Object.entries(moduleValues);
        const enabledEntry = entries.find(([, value]) => value === true);
        return enabledEntry ? { key: enabledEntry[0] } : null;
      }
      return null;
    })
    .find((result) => result !== null);

  return foundModule || null; // Return null if no enabled module is found
}

// Provider component that wraps the app to provide access to privilege-related state
export function PrivilegeProvider({ children }) {
  const [role, setRole] = useState(() => {
    // Retrieve role from localStorage on initial load
    const savedRole = localStorage.getItem('role');
    return savedRole ? JSON.parse(savedRole) : null;
  });
  const [modules, setModules] = useState(() => {
    // Retrieve modules from localStorage on initial load
    const savedModules = localStorage.getItem('modules');
    return savedModules ? JSON.parse(savedModules) : {};
  });

  useEffect(() => {
    // Save role to localStorage whenever it changes
    if (role !== null) {
      localStorage.setItem('role', JSON.stringify(role));
    }
  }, [role]);

  useEffect(() => {
    // Save modules to localStorage whenever they change
    if (modules) {
      localStorage.setItem('modules', JSON.stringify(modules));
    }
  }, [modules]);

  const contextValue = useMemo(() => ({
    role,
    setRole,
    modules,
    setModules,
  }), [role, modules]);

  return (
    <PrivilegeContext.Provider value={contextValue}>
      {children}
    </PrivilegeContext.Provider>
  );
}

export const usePrivilege = () => useContext(PrivilegeContext);
